import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, Calendar, Phone, Star, CheckSquare, FileText } from 'lucide-angular';

export interface ScheduleItem {
  id: string;
  title: string;
  time: string;
  date?: string;
  type: 'meeting' | 'call' | 'event' | 'task' | 'review';
  priority: 'high' | 'medium' | 'low';
  completed?: boolean;
}

@Component({
  selector: 'app-upcoming-schedule',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="bg-white rounded-xl border border-gray-200 p-6">
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-lg font-semibold text-gray-900 font-outfit">{{ title }}</h3>
        <button class="text-sm text-brand-500 hover:text-brand-600 font-medium transition-colors">
          View All
        </button>
      </div>
      
      <div class="space-y-4">
        @for (item of scheduleItems; track item.id) {
          <div class="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
               [class]="item.completed ? 'opacity-60' : ''">
            <!-- Priority indicator -->
            <div 
              class="w-2 h-2 rounded-full mt-2 flex-shrink-0"
              [class]="getPriorityColor(item.priority, item.type)">
            </div>
            
            <!-- Content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <p class="text-sm font-medium text-gray-900"
                     [class]="item.completed ? 'line-through text-gray-500' : ''">
                    {{ item.title }}
                  </p>
                  <div class="flex items-center mt-1 space-x-2">
                    <span class="text-xs text-gray-500">{{ item.time }}</span>
                    @if (item.date) {
                      <span class="text-xs text-gray-400">•</span>
                      <span class="text-xs text-gray-500">{{ item.date }}</span>
                    }
                  </div>
                </div>
                
                <!-- Type badge -->
                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2"
                      [class]="getTypeBadgeClass(item.type)">
                  {{ getTypeLabel(item.type) }}
                </span>
              </div>
              
              <!-- Progress bar for tasks -->
              @if (item.type === 'task') {
                <div class="mt-2">
                  <div class="w-full bg-gray-200 rounded-full h-1">
                    <div 
                      class="h-1 rounded-full transition-all duration-300"
                      [class]="item.completed ? 'bg-success-500' : 'bg-brand-500'"
                      [style.width.%]="item.completed ? 100 : 60">
                    </div>
                  </div>
                </div>
              }
            </div>
            
            <!-- Completion checkbox for tasks -->
            @if (item.type === 'task') {
              <div class="flex-shrink-0">
                <input 
                  type="checkbox" 
                  [checked]="item.completed"
                  (change)="toggleCompletion(item)"
                  class="rounded border-gray-300 text-brand-500 focus:ring-brand-500">
              </div>
            }
          </div>
        }
      </div>
      
      @if (scheduleItems.length === 0) {
        <div class="text-center py-8">
          <div class="text-gray-400 mb-2">
            <lucide-icon [img]="Calendar" size="48" class="mx-auto"></lucide-icon>
          </div>
          <p class="text-sm text-gray-500">No upcoming events</p>
        </div>
      }
    </div>
  `,
  styles: ``
})
export class UpcomingScheduleComponent {
  @Input() title: string = 'Upcoming Schedule';
  @Input() scheduleItems: ScheduleItem[] = [];

  // Icon references
  protected readonly Calendar = Calendar;
  protected readonly Phone = Phone;
  protected readonly Star = Star;
  protected readonly CheckSquare = CheckSquare;
  protected readonly FileText = FileText;

  toggleCompletion(item: ScheduleItem): void {
    item.completed = !item.completed;
  }

  getPriorityColor(priority: string, type: string): string {
    if (type === 'task') {
      return priority === 'high' ? 'bg-error-500' : 
             priority === 'medium' ? 'bg-warning-500' : 'bg-success-500';
    }
    
    const colors = {
      'meeting': 'bg-brand-500',
      'call': 'bg-blue-light-500',
      'event': 'bg-orange-500',
      'review': 'bg-success-500'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-500';
  }

  getTypeBadgeClass(type: string): string {
    const classes = {
      'meeting': 'bg-brand-100 text-brand-800',
      'call': 'bg-blue-light-100 text-blue-light-800',
      'event': 'bg-orange-100 text-orange-800',
      'task': 'bg-gray-100 text-gray-800',
      'review': 'bg-success-100 text-success-800'
    };
    return classes[type as keyof typeof classes] || 'bg-gray-100 text-gray-800';
  }

  getTypeLabel(type: string): string {
    const labels = {
      'meeting': 'Meeting',
      'call': 'Call',
      'event': 'Event',
      'task': 'Task',
      'review': 'Review'
    };
    return labels[type as keyof typeof labels] || type;
  }
}